/**
 * Proximity Page - Primary Navigation Tab
 * ========================================
 * Main proximity discovery screen with ON/OFF states.
 * Shows current place and nearby NearU members.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Radio, Shield, User, ChevronRight, 
  Eye, EyeOff, AlertCircle, Check, X, Loader2
} from 'lucide-react';
import { useProximity } from '../context/ProximityContext';
import NearULogo from '../components/NearULogo';
import { getFirstName } from '../utils/nameUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProximityPage = () => {
  const navigate = useNavigate();
  const {
    proximityEnabled,
    permissionStatus,
    nearbyMembers,
    nearbyCount,
    loadingNearby,
    enableProximity,
    disableProximity,
    fetchNearbyMembers,
    fetchProximityStatus
  } = useProximity();

  const [isDark, setIsDark] = useState(true);
  const [currentPlace, setCurrentPlace] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [showPlaceSelector, setShowPlaceSelector] = useState(false);
  const [detectingPlace, setDetectingPlace] = useState(false);
  const [turningOn, setTurningOn] = useState(false);
  const [turningOff, setTurningOff] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [exitNotice, setExitNotice] = useState(null);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    window.addEventListener('storage', checkTheme);
    return () => window.removeEventListener('storage', checkTheme);
  }, []);

  useEffect(() => {
    fetchProximityStatus();
    if (proximityEnabled) {
      fetchCurrentPlace();
    }
  }, [proximityEnabled]);

  // Check if user has left the place (auto turn-off)
  useEffect(() => {
    if (!proximityEnabled || !currentPlace) return;

    const checkStillAtPlace = async () => {
      try {
        const res = await fetch(`${API}/proximity/check-location`);
        if (res.ok) {
          const data = await res.json();
          if (data.left_place) {
            // User has left the place
            setExitNotice({
              placeName: currentPlace.name,
              message: `You left ${currentPlace.name}`
            });
            await disableProximity();
            setCurrentPlace(null);
            
            // Clear notice after 5 seconds
            setTimeout(() => setExitNotice(null), 5000);
          }
        }
      } catch (error) {
        console.error('Error checking location:', error);
      }
    };

    const interval = setInterval(checkStillAtPlace, 30000);
    return () => clearInterval(interval);
  }, [proximityEnabled, currentPlace]);

  const fetchCurrentPlace = async () => {
    try {
      const res = await fetch(`${API}/proximity/current-place`);
      if (res.ok) {
        const data = await res.json();
        if (data.place) {
          setCurrentPlace(data.place);
        }
      }
    } catch (error) {
      console.error('Error fetching current place:', error);
    }
  };

  const detectNearbyPlaces = async (latitude, longitude) => {
    try {
      const res = await fetch(`${API}/proximity/detect-places`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude, longitude })
      });
      if (res.ok) {
        const data = await res.json();
        return data.places || [];
      }
    } catch (error) {
      console.error('Error detecting places:', error);
    }
    return [];
  };

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(139, 105, 20, 0.2)',
    accent: isDark ? '#D4AF37' : '#8B6914',
    gold: isDark ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'linear-gradient(135deg, #A57C33 0%, #8B6914 100%)',
    liveGreen: '#4CAF50',
    liveGlow: 'rgba(76, 175, 80, 0.3)'
  };

  const handleTurnOn = async () => {
    // Show consent on first activation
    const hasSeenConsent = localStorage.getItem('nearU_proximity_consent_v1');
    if (!hasSeenConsent) {
      setShowConsent(true);
      return;
    }

    await startProximityFlow();
  };

  const handleConsentAccept = async () => {
    localStorage.setItem('nearU_proximity_consent_v1', 'true');
    setShowConsent(false);
    await startProximityFlow();
  };

  const startProximityFlow = async () => {
    setTurningOn(true);
    setDetectingPlace(true);

    try {
      // Get current position
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000
        });
      });

      const { latitude, longitude } = position.coords;

      // Detect nearby places
      const places = await detectNearbyPlaces(latitude, longitude);
      
      if (places.length > 0) {
        // Show detected place for confirmation
        setNearbyPlaces(places);
        setCurrentPlace(places[0]); // Most likely place
        setShowPlaceSelector(true);
      } else {
        // No places detected, use generic location
        setCurrentPlace({
          id: 'current_location',
          name: 'Current Location',
          type: 'general',
          address: ''
        });
        await confirmPlace({ id: 'current_location', name: 'Current Location' });
      }
    } catch (error) {
      console.error('Error starting proximity:', error);
      alert('Could not get your location. Please enable location access and try again.');
    } finally {
      setTurningOn(false);
      setDetectingPlace(false);
    }
  };

  const confirmPlace = async (place) => {
    setShowPlaceSelector(false);
    setCurrentPlace(place);

    try {
      // Enable proximity with confirmed place
      const result = await enableProximity();
      
      if (result.success) {
        // Send confirmed place to server
        await fetch(`${API}/proximity/set-place`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            place_id: place.id,
            place_name: place.name,
            place_type: place.type
          })
        });
        
        // Fetch nearby members
        await fetchNearbyMembers();
      } else {
        alert(result.error || 'Failed to enable proximity');
        setCurrentPlace(null);
      }
    } catch (error) {
      console.error('Error confirming place:', error);
    }
  };

  const handleTurnOff = async () => {
    setTurningOff(true);
    try {
      await disableProximity();
      setCurrentPlace(null);
    } catch (error) {
      console.error('Error turning off proximity:', error);
    } finally {
      setTurningOff(false);
    }
  };

  const handleSelectDifferentPlace = () => {
    // Show place selector with all nearby options
    setShowPlaceSelector(true);
  };

  // Render consent modal
  const renderConsentModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      backdropFilter: 'blur(10px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: colors.cardBg,
        borderRadius: '20px',
        maxWidth: '400px',
        width: '100%',
        padding: '32px 24px',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            background: 'rgba(215, 181, 109, 0.15)',
            border: '2px solid rgba(215, 181, 109, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Shield size={32} color={colors.accent} />
          </div>
          
          <h2 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '22px',
            color: colors.text,
            marginBottom: '12px'
          }}>
            Proximity Discovery
          </h2>
          
          <p style={{
            color: colors.textMuted,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            When enabled, other NearU members at your location can see that you're nearby.
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          {[
            { icon: Eye, text: 'Only visible to other NearU members who are also nearby' },
            { icon: MapPin, text: 'Your exact location is never shown to others' },
            { icon: Shield, text: 'You can turn off visibility at any time' }
          ].map((item, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '10px',
              background: 'rgba(215, 181, 109, 0.05)',
              borderRadius: '10px',
              marginBottom: '8px'
            }}>
              <item.icon size={18} color={colors.accent} style={{ marginTop: '2px', flexShrink: 0 }} />
              <span style={{ color: colors.text, fontSize: '13px', lineHeight: '1.4' }}>
                {item.text}
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowConsent(false)}
            style={{
              flex: 1,
              padding: '14px',
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              color: colors.textMuted,
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConsentAccept}
            style={{
              flex: 2,
              padding: '14px',
              background: colors.gold,
              border: 'none',
              borderRadius: '12px',
              color: '#2C1B13',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Enable Proximity
          </button>
        </div>

        <p style={{
          color: colors.textMuted,
          fontSize: '11px',
          textAlign: 'center',
          marginTop: '16px'
        }}>
          By enabling, you agree to NearU's{' '}
          <a href="/legal/privacy" style={{ color: colors.accent }}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );

  // Render place selector modal
  const renderPlaceSelector = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      backdropFilter: 'blur(10px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: colors.cardBg,
        borderRadius: '20px',
        maxWidth: '400px',
        width: '100%',
        padding: '24px',
        border: `1px solid ${colors.border}`,
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'rgba(215, 181, 109, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <MapPin size={28} color={colors.accent} />
          </div>
          
          <h2 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '20px',
            color: colors.text,
            marginBottom: '8px'
          }}>
            {nearbyPlaces.length > 0 && currentPlace ? 'Are you here?' : 'Where are you?'}
          </h2>
        </div>

        {/* Primary detected place */}
        {currentPlace && (
          <button
            onClick={() => confirmPlace(currentPlace)}
            style={{
              width: '100%',
              padding: '16px',
              background: 'rgba(76, 175, 80, 0.1)',
              border: '2px solid rgba(76, 175, 80, 0.4)',
              borderRadius: '14px',
              cursor: 'pointer',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ 
                  color: colors.text, 
                  fontSize: '17px', 
                  fontWeight: '700',
                  marginBottom: '4px'
                }}>
                  {currentPlace.name}
                </div>
                {currentPlace.type && (
                  <div style={{ color: colors.textMuted, fontSize: '13px' }}>
                    {currentPlace.type}
                  </div>
                )}
              </div>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: colors.gold,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check size={18} color="#2C1B13" />
              </div>
            </div>
          </button>
        )}

        {/* Other nearby places */}
        {nearbyPlaces.length > 1 && (
          <>
            <p style={{ 
              color: colors.textMuted, 
              fontSize: '12px', 
              marginBottom: '12px',
              textAlign: 'center'
            }}>
              Or choose another nearby place
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {nearbyPlaces.slice(1).map((place) => (
                <button
                  key={place.id}
                  onClick={() => confirmPlace(place)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: colors.cardBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div style={{ color: colors.text, fontSize: '15px', fontWeight: '600' }}>
                      {place.name}
                    </div>
                    {place.type && (
                      <div style={{ color: colors.textMuted, fontSize: '12px' }}>
                        {place.type}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={18} color={colors.textMuted} />
                </button>
              ))}
            </div>
          </>
        )}

        <button
          onClick={() => {
            setShowPlaceSelector(false);
            setCurrentPlace(null);
          }}
          style={{
            width: '100%',
            padding: '12px',
            background: 'transparent',
            border: 'none',
            color: colors.textMuted,
            fontSize: '14px',
            cursor: 'pointer',
            marginTop: '16px'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Render exit notice
  const renderExitNotice = () => (
    <div style={{
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      borderRadius: '14px',
      padding: '16px 24px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'rgba(255, 193, 7, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <MapPin size={18} color="#FFC107" />
      </div>
      <div>
        <div style={{ color: colors.text, fontSize: '14px', fontWeight: '600' }}>
          Proximity turned off
        </div>
        <div style={{ color: colors.textMuted, fontSize: '12px' }}>
          {exitNotice.message}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '100px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <NearULogo size="small" showText={true} />
      </div>

      {/* Page Title */}
      <div style={{ padding: '0 20px 24px', textAlign: 'center' }}>
        <h1 style={{ 
          fontFamily: 'Cinzel, serif', 
          fontSize: '28px',
          fontWeight: '600',
          color: colors.text,
          margin: '0 0 8px 0'
        }}>
          Proximity
        </h1>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px',
          margin: 0
        }}>
          Discover who's nearby
        </p>
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 16px' }}>
        
        {/* ============================================ */}
        {/* PROXIMITY OFF STATE */}
        {/* ============================================ */}
        {!proximityEnabled && (
          <div data-testid="proximity-off-state">
            {/* Status Card */}
            <div style={{
              background: colors.cardBg,
              borderRadius: '20px',
              border: `1px solid ${colors.border}`,
              padding: '32px 24px',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              {/* Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(138, 115, 85, 0.1)',
                border: `2px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <Radio size={36} color={colors.textMuted} />
              </div>

              {/* Status */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: 'rgba(138, 115, 85, 0.1)',
                borderRadius: '20px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: colors.textMuted
                }} />
                <span style={{ 
                  color: colors.textMuted, 
                  fontSize: '14px', 
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Off
                </span>
              </div>

              <h2 style={{
                fontFamily: 'Cinzel, serif',
                fontSize: '22px',
                color: colors.text,
                margin: '0 0 8px 0'
              }}>
                Visible Nearby
              </h2>

              <p style={{
                color: colors.textMuted,
                fontSize: '14px',
                lineHeight: '1.6',
                marginBottom: '24px',
                maxWidth: '280px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                You are not currently visible to nearby NearU members.
              </p>

              {/* Turn On Button */}
              <button
                onClick={handleTurnOn}
                disabled={turningOn}
                data-testid="turn-on-btn"
                style={{
                  width: '100%',
                  padding: '18px',
                  background: colors.gold,
                  border: 'none',
                  borderRadius: '14px',
                  color: '#2C1B13',
                  fontSize: '17px',
                  fontWeight: '700',
                  cursor: turningOn ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 20px rgba(215, 181, 109, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                {turningOn ? (
                  <>
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    {detectingPlace ? 'Finding your location...' : 'Turning on...'}
                  </>
                ) : (
                  'TURN ON'
                )}
              </button>
            </div>

            {/* Explanation */}
            <div style={{
              padding: '20px',
              background: 'rgba(215, 181, 109, 0.05)',
              borderRadius: '14px',
              border: `1px solid ${colors.border}`
            }}>
              <p style={{
                color: colors.text,
                fontSize: '14px',
                lineHeight: '1.6',
                margin: 0,
                textAlign: 'center'
              }}>
                Turn on Proximity to discover participating NearU members at the place you're currently visiting.
              </p>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* PROXIMITY ON STATE */}
        {/* ============================================ */}
        {proximityEnabled && (
          <div data-testid="proximity-on-state">
            {/* Status Card */}
            <div style={{
              background: colors.cardBg,
              borderRadius: '20px',
              border: `1px solid ${colors.liveGreen}40`,
              padding: '24px',
              marginBottom: '20px',
              boxShadow: `0 0 40px ${colors.liveGlow}`
            }}>
              {/* Live Status */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: colors.liveGreen,
                  boxShadow: `0 0 12px ${colors.liveGreen}`,
                  animation: 'pulse 2s infinite'
                }} />
                <span style={{
                  color: colors.liveGreen,
                  fontSize: '15px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px'
                }}>
                  Visible Now
                </span>
              </div>

              {/* Current Place */}
              {currentPlace && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <h2 style={{
                    fontFamily: 'Cinzel, serif',
                    fontSize: '24px',
                    color: colors.text,
                    margin: '0 0 4px 0'
                  }}>
                    {currentPlace.name}
                  </h2>
                  {currentPlace.type && (
                    <p style={{ color: colors.textMuted, fontSize: '13px', margin: 0 }}>
                      {currentPlace.type}
                    </p>
                  )}
                </div>
              )}

              <p style={{
                color: colors.textMuted,
                fontSize: '13px',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                Participating NearU members here can see you.
              </p>

              {/* Turn Off Button */}
              <button
                onClick={handleTurnOff}
                disabled={turningOff}
                data-testid="turn-off-btn"
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: '12px',
                  color: '#F44336',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: turningOff ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {turningOff ? (
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <EyeOff size={18} />
                )}
                TURN OFF
              </button>
            </div>

            {/* Here Now Section */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <h3 style={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: '18px',
                  color: colors.text,
                  margin: 0
                }}>
                  Here Now
                </h3>
                <span style={{
                  padding: '4px 10px',
                  background: 'rgba(76, 175, 80, 0.15)',
                  borderRadius: '12px',
                  color: colors.liveGreen,
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {nearbyCount} {nearbyCount === 1 ? 'member' : 'members'}
                </span>
              </div>

              {loadingNearby ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: colors.textMuted
                }}>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              ) : nearbyMembers.length === 0 ? (
                <div style={{
                  background: colors.cardBg,
                  borderRadius: '16px',
                  border: `1px solid ${colors.border}`,
                  padding: '32px 24px',
                  textAlign: 'center'
                }}>
                  <Radio size={40} color={colors.textMuted} style={{ opacity: 0.4, marginBottom: '12px' }} />
                  <p style={{ color: colors.textMuted, fontSize: '14px', margin: 0 }}>
                    No other NearU members nearby right now
                  </p>
                </div>
              ) : (
                <div style={{
                  background: colors.cardBg,
                  borderRadius: '16px',
                  border: `1px solid ${colors.border}`,
                  overflow: 'hidden'
                }}>
                  {nearbyMembers.map((member, index) => (
                    <div
                      key={member.id}
                      onClick={() => navigate(`/user/${member.id}`)}
                      data-testid={`nearby-member-${member.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        padding: '16px',
                        cursor: 'pointer',
                        borderBottom: index < nearbyMembers.length - 1 ? `1px solid ${colors.border}` : 'none'
                      }}
                    >
                      {/* Avatar */}
                      {member.photo ? (
                        <img 
                          src={member.photo}
                          alt={member.name}
                          style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid rgba(76, 175, 80, 0.4)'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: 'rgba(215, 181, 109, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid rgba(76, 175, 80, 0.4)'
                        }}>
                          <User size={28} color={colors.accent} />
                        </div>
                      )}

                      {/* Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px'
                        }}>
                          <span style={{ 
                            color: colors.text, 
                            fontSize: '16px', 
                            fontWeight: '600' 
                          }}>
                            {getFirstName(member.name)}
                          </span>
                          {member.age && (
                            <span style={{ 
                              color: colors.textMuted, 
                              fontSize: '14px' 
                            }}>
                              {member.age}
                            </span>
                          )}
                        </div>
                        {member.bio && (
                          <p style={{
                            color: colors.textMuted,
                            fontSize: '13px',
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {member.bio}
                          </p>
                        )}
                      </div>

                      {/* Live Indicator */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        background: 'rgba(76, 175, 80, 0.1)',
                        borderRadius: '12px'
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: colors.liveGreen
                        }} />
                        <span style={{
                          color: colors.liveGreen,
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          HERE
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showConsent && renderConsentModal()}
      {showPlaceSelector && renderPlaceSelector()}
      {exitNotice && renderExitNotice()}

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProximityPage;
